import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
// Mock data imports removed - using backend data only
import { bookingsAPI, leadsAPI } from '../services/api';
import { formatCurrency, formatCurrencyCompact, formatDate } from '../utils/currency';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import AddToHomeScreen from '../components/AddToHomeScreen';
import useFinancialVisibility from '../hooks/useFinancialVisibility';
import { 
  CalendarCheck, 
  Clock,
  DollarSign, 
  TrendingUp, 
  Users, 
  Activity,
  ArrowRight,
  BarChart3,
  PieChart,
  UserCheck,
  Phone,
  Calendar,
  Eye,
  RefreshCw,
  Crown,
  AlertTriangle,
  Sparkles,
  Info
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = React.useState([]);
  const [leads, setLeads] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [followUpsExpanded, setFollowUpsExpanded] = React.useState(false);
  const [upcomingExpanded, setUpcomingExpanded] = React.useState(false);
  const [callDialogOpen, setCallDialogOpen] = React.useState(false);
  const [selectedLead, setSelectedLead] = React.useState(null);
  const [syncDialogOpen, setSyncDialogOpen] = React.useState(false);
  const [isSyncing, setIsSyncing] = React.useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = React.useState(null);
  const { toast } = useToast();
  const { currentUser } = useAuth();
  const hideFinancials = useFinancialVisibility();

  React.useEffect(() => {
    loadDashboardData();
    loadSubscriptionStatus();
  }, []);

  const loadSubscriptionStatus = async () => {
    // Skip API call in offline mode - use cached user subscription
    if (!navigator.onLine || currentUser?.isOfflineSession) {
      if (currentUser?.subscription) {
        const sub = currentUser.subscription;
        const expiryDate = sub.expiryDate ? new Date(sub.expiryDate) : null;
        const now = new Date();
        const isExpired = expiryDate ? expiryDate < now : false;
        const daysRemaining = expiryDate ? Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24)) : null;
        
        setSubscriptionStatus({
          hasSubscription: sub.status === 'active' && !isExpired,
          status: isExpired ? 'expired' : sub.status,
          plan: sub.plan,
          expiryDate: sub.expiryDate,
          daysRemaining: daysRemaining > 0 ? daysRemaining : 0,
          isOffline: true
        });
      }
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/subscriptions/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSubscriptionStatus(data);
      }
    } catch (error) {
      console.error('Failed to load subscription status:', error);
      // Fallback to cached subscription data on error
      if (currentUser?.subscription) {
        const sub = currentUser.subscription;
        const expiryDate = sub.expiryDate ? new Date(sub.expiryDate) : null;
        const now = new Date();
        const isExpired = expiryDate ? expiryDate < now : false;
        const daysRemaining = expiryDate ? Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24)) : null;
        
        setSubscriptionStatus({
          hasSubscription: sub.status === 'active' && !isExpired,
          status: isExpired ? 'expired' : sub.status,
          plan: sub.plan,
          expiryDate: sub.expiryDate,
          daysRemaining: daysRemaining > 0 ? daysRemaining : 0,
          isOffline: true
        });
      }
    }
  };

  const getSubscriptionBadge = () => {
    if (!subscriptionStatus) return null;
    
    if (currentUser?.role === 'master') {
      return { text: 'Master Account', color: 'bg-purple-600', icon: Crown };
    }
    
    if (!subscriptionStatus.hasSubscription) {
      if (subscriptionStatus.status === 'expired') {
        return { text: 'Subscription Expired', color: 'bg-red-500', icon: AlertTriangle };
      }
      return { text: 'No Subscription', color: 'bg-gray-500', icon: AlertTriangle };
    }
    
    // Plan durations in days based on plan ID
    const planDurationsByPlanId = {
      'PLAN_TRIAL': 15,
      'PLAN_FREE_TRIAL': 15,
      'PLAN_1_MONTH': 30,
      'PLAN_3_MONTHS': 90,
      'PLAN_6_MONTHS': 180,
      'PLAN_12_MONTHS': 365,
      'PLAN_LIFETIME': 1825,
      'PLAN_5_YEARS': 1825
    };
    
    // Plan durations based on display name (for when plan ID is custom like "Professional")
    const planDurationsByName = {
      'Trial': 15,
      'Trial Plan': 15,
      '1 Month': 30,
      '3 Months': 90,
      '6 Months': 180,
      '12 Months': 365,
      '1 Year': 365,
      '5 Years': 1825,
      'Lifetime': 1825
    };
    
    const planDisplayNames = {
      'PLAN_TRIAL': 'Trial',
      'PLAN_FREE_TRIAL': 'Trial',
      'PLAN_1_MONTH': '1 Month',
      'PLAN_3_MONTHS': '3 Months',
      'PLAN_6_MONTHS': '6 Months',
      'PLAN_12_MONTHS': '12 Months',
      'PLAN_LIFETIME': '5 Years',
      'PLAN_5_YEARS': '5 Years'
    };
    
    const daysRemaining = subscriptionStatus.daysRemaining || 0;
    const displayPlanName = subscriptionStatus.planName || planDisplayNames[subscriptionStatus.plan] || 'Active';
    
    // Get expected duration - first try by plan ID, then by display name
    let expectedDuration = planDurationsByPlanId[subscriptionStatus.plan] || 0;
    if (expectedDuration === 0 && subscriptionStatus.planName) {
      expectedDuration = planDurationsByName[subscriptionStatus.planName] || 0;
    }
    
    // Check if days remaining exceeds expected plan duration (with 10% buffer)
    // Also check the dataInconsistent flag from backend
    const isCustomExtended = subscriptionStatus.dataInconsistent || 
                             (expectedDuration > 0 && daysRemaining > (expectedDuration * 1.1));
    
    let planName;
    if (isCustomExtended) {
      planName = 'Custom Plan';
    } else {
      planName = displayPlanName;
    }
    
    if (subscriptionStatus.plan === 'PLAN_LIFETIME' || subscriptionStatus.plan === 'PLAN_5_YEARS') {
      return { text: planName, color: 'bg-gradient-to-r from-purple-600 to-indigo-600', icon: Crown };
    }
    
    if (subscriptionStatus.plan?.includes('TRIAL') || subscriptionStatus.plan === 'PLAN_FREE_TRIAL') {
      return { text: `${planName} (${daysRemaining} days left)`, color: 'bg-green-600', icon: Sparkles };
    }
    
    return { 
      text: `${planName} (${daysRemaining} days left)`, 
      color: isCustomExtended ? 'bg-orange-600' : 'bg-teal-600', 
      icon: Crown 
    };
  };

  const loadDashboardData = async () => {
    setIsLoading(true);
    
    // Load bookings and leads in parallel for faster loading
    const [bookingsResult, leadsResult] = await Promise.allSettled([
      bookingsAPI.getAll(),
      leadsAPI.getAll()
    ]);
    
    // Process bookings
    if (bookingsResult.status === 'fulfilled') {
      const mappedBookings = bookingsResult.value.map(booking => ({
        id: booking.id,
        clientName: booking.customerName,
        customerName: booking.customerName,
        venue: booking.venueName,
        venueName: booking.venueName,
        eventDate: booking.eventDate,
        eventType: booking.eventType,
        status: booking.status?.toLowerCase() || 'confirmed',
        totalAmount: booking.amount,
        amount: booking.amount,
        advancePaid: booking.advancePaid || 0,
        payments: booking.payments || [],
        guests: booking.guests || 0
      }));
      setBookings(mappedBookings);
    } else {
      console.error('Failed to load bookings:', bookingsResult.reason);
      setBookings([]);
    }
    
    // Process leads
    if (leadsResult.status === 'fulfilled') {
      setLeads(leadsResult.value);
    } else {
      console.error('Failed to load leads:', leadsResult.reason);
      setLeads([]);
    }
    
    setIsLoading(false);
  };

  const handleSyncAndClear = async () => {
    setIsSyncing(true);
    
    try {
      // Step 1: Upload any pending local data (if exists)
      toast({
        title: 'Syncing...',
        description: 'Uploading local data to server...'
      });
      
      // Step 2: Clear all local storage except auth data
      const preserveKeys = ['token', 'currentUser'];
      const keysToRemove = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && !preserveKeys.includes(key)) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      toast({
        title: 'Storage Cleared',
        description: 'Local storage cleared successfully.'
      });
      
      // Step 3: Refresh data from backend
      await loadDashboardData();
      
      // Step 4: Clear any cache
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }
      
      toast({
        title: 'Sync Complete!',
        description: 'Your data is now fully synced with the central server. All devices will show the same data.',
        duration: 5000
      });
      
      setSyncDialogOpen(false);
      
      // Reload page to ensure clean state
      setTimeout(() => {
        window.location.reload();
      }, 1000);
      
    } catch (error) {
      console.error('Sync error:', error);
      toast({
        title: 'Sync Failed',
        description: 'Failed to sync data. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSyncing(false);
    }
  };
  
  const upcomingBookings = bookings
    .filter(b => new Date(b.eventDate) >= new Date() && b.status === 'confirmed')
    .sort((a, b) => new Date(a.eventDate) - new Date(b.eventDate))
    .slice(0, 5);

  // Calculate this month's bookings
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();
  
  const thisMonthBookings = bookings.filter(b => {
    const bookingDate = new Date(b.eventDate);
    return bookingDate.getMonth() === currentMonth && bookingDate.getFullYear() === currentYear;
  });
  
  // Bookings this year
  const thisYearBookings = bookings.filter(b => {
    const bookingDate = new Date(b.eventDate);
    return bookingDate.getFullYear() === currentYear;
  });

  // Active leads (not converted)
  const activeLeads = leads.filter(l => !l.converted);

  // Leads needing follow-up this month
  const followUpLeads = leads.filter(l => {
    if (!l.followupDate || l.converted) return false;
    const followUpDate = new Date(l.followupDate);
    return followUpDate.getMonth() === currentMonth && followUpDate.getFullYear() === currentYear;
  }).sort((a, b) => new Date(a.followupDate) - new Date(b.followupDate));

  // Calculate real dashboard statistics from bookings
  const calculateDashboardStats = () => {
    // Total revenue from all confirmed bookings
    const totalRevenue = bookings
      .filter(b => b.status === 'confirmed')
      .reduce((sum, b) => sum + (b.totalAmount || 0), 0);
    
    // Revenue this month
    const revenueThisMonth = thisMonthBookings
      .filter(b => b.status === 'confirmed')
      .reduce((sum, b) => sum + (b.totalAmount || 0), 0);
    
    // Pending payments (total amount - total paid including advance + additional payments)
    const pendingPayments = bookings
      .filter(b => b.status === 'confirmed')
      .reduce((sum, b) => {
        const totalAmount = Number(b.totalAmount) || 0;
        const advancePaid = Number(b.advancePaid) || 0;
        const additionalPayments = (b.payments || []).reduce((pSum, p) => pSum + Number(p.amount || 0), 0);
        const totalPaid = advancePaid + additionalPayments;
        const balance = Math.max(0, totalAmount - totalPaid); // Ensure no negative balance
        return sum + balance;
      }, 0);
    
    // Confirmed bookings this month
    const confirmedBookingsThisMonth = thisMonthBookings.filter(b => b.status === 'confirmed').length;
    
    return {
      totalRevenue,
      revenueThisMonth,
      pendingPayments,
      confirmedBookingsThisMonth,
      activeLeads: activeLeads.length
    };
  };

  const dashboardStats = calculateDashboardStats();

  const stats = [
    {
      title: 'Total Bookings This Month',
      value: thisMonthBookings.length,
      icon: CalendarCheck,
      color: 'from-teal-500 to-cyan-600',
      bgColor: 'bg-teal-50 dark:bg-teal-900/20',
      textColor: 'text-teal-600',
      link: '/bookings'
    },
    {
      title: 'Active Leads',
      value: activeLeads.length,
      icon: UserCheck,
      color: 'from-blue-500 to-indigo-600',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600',
      link: '/leads'
    },
    {
      title: 'Lead Follow-ups This Month',
      value: followUpLeads.length,
      icon: Phone,
      color: 'from-orange-500 to-amber-600',
      bgColor: 'bg-orange-50',
      textColor: 'text-orange-600',
      link: '#follow-ups'
    },
    {
      title: 'Upcoming Bookings',
      value: upcomingBookings.length,
      icon: Calendar,
      color: 'from-purple-500 to-pink-600',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-600',
      link: '#upcoming'
    }
  ];

  // Filter revenue stats based on financial visibility
  const revenueStats = hideFinancials ? [
    {
      title: 'Bookings This Year',
      value: thisYearBookings.length,
      icon: CalendarCheck,
      color: 'text-purple-600',
      subtitle: currentYear.toString()
    }
  ] : [
    {
      title: 'Total Revenue',
      value: formatCurrencyCompact(dashboardStats.totalRevenue),
      icon: DollarSign,
      color: 'text-teal-600',
      subtitle: `From ${bookings.filter(b => b.status === 'confirmed').length} bookings`
    },
    {
      title: 'This Month',
      value: formatCurrencyCompact(dashboardStats.revenueThisMonth),
      icon: TrendingUp,
      color: 'text-cyan-600',
      subtitle: `${dashboardStats.confirmedBookingsThisMonth} confirmed`
    },
    {
      title: 'Pending Payments',
      value: formatCurrencyCompact(dashboardStats.pendingPayments),
      icon: Clock,
      color: 'text-orange-600',
      subtitle: 'Balance due'
    },
    {
      title: 'Bookings This Year',
      value: thisYearBookings.length,
      icon: CalendarCheck,
      color: 'text-purple-600',
      subtitle: currentYear.toString()
    }
  ];

  const handleStatClick = (link) => {
    if (link.startsWith('#')) {
      // Scroll to section
      const element = document.querySelector(link);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      navigate(link);
    }
  };

  const handleLeadClick = (lead) => {
    setSelectedLead(lead);
    setCallDialogOpen(true);
  };

  const handleCallConfirm = () => {
    if (selectedLead) {
      // Initiate phone call
      window.location.href = `tel:${selectedLead.phone}`;
      setCallDialogOpen(false);
      setSelectedLead(null);
    }
  };

  const handleCallCancel = () => {
    setCallDialogOpen(false);
    setSelectedLead(null);
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Subscription Status Banner */}
      {subscriptionStatus && (currentUser?.role === 'admin' || currentUser?.role === 'master') && (
        <div className={`rounded-xl p-3 sm:p-4 ${
          subscriptionStatus.status === 'expired' || !subscriptionStatus.hasSubscription 
            ? 'bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 border border-red-200 dark:border-red-800' 
            : 'bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 border border-teal-200 dark:border-teal-800'
        }`}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              {(() => {
                const badge = getSubscriptionBadge();
                if (!badge) return null;
                const Icon = badge.icon;
                return (
                  <>
                    <div className={`p-2 rounded-lg ${badge.color} text-white`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-foreground">{badge.text}</p>
                      {subscriptionStatus.expiryDate && subscriptionStatus.status !== 'expired' && (
                        <p className="text-xs text-gray-600 dark:text-muted-foreground">
                          Valid until {new Date(subscriptionStatus.expiryDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                      )}
                      {subscriptionStatus.status === 'expired' && (
                        <p className="text-xs text-red-600 dark:text-red-400">Please renew to continue using all features</p>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
            {(subscriptionStatus.status === 'expired' || !subscriptionStatus.hasSubscription || 
              (subscriptionStatus.daysRemaining && subscriptionStatus.daysRemaining <= 7)) && 
              currentUser?.role !== 'master' && (
              <Button 
                onClick={() => navigate('/choose-plan', { state: { renewal: subscriptionStatus.status === 'expired' } })}
                className={`${
                  subscriptionStatus.status === 'expired' 
                    ? 'bg-red-600 hover:bg-red-700' 
                    : 'bg-teal-600 hover:bg-teal-700'
                }`}
                size="sm"
              >
                {subscriptionStatus.status === 'expired' ? 'Renew Now' : 'Upgrade Plan'}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Demo Account Banner */}
      {currentUser?.email?.toLowerCase() === 'demo@myvenuemate.in' && (
        <div 
          className="rounded-xl p-3 sm:p-4 bg-gradient-to-r from-amber-500/10 to-orange-500/10 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-500/30 dark:border-amber-700/50"
          data-testid="demo-banner"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500 text-white shrink-0">
              <Info className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-amber-700 dark:text-amber-400">You&apos;re viewing demo data</p>
              <p className="text-xs text-amber-600 dark:text-amber-500">
                Changes are disabled. <a href="/register" className="underline hover:text-amber-800 dark:hover:text-amber-300 font-medium">Register for a free trial</a> to create your own account.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header with Sync & Refresh */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-foreground">Dashboard</h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-muted-foreground mt-1">Overview of your venue management</p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <Button 
            onClick={() => setSyncDialogOpen(true)}
            variant="outline"
            className="flex items-center gap-2 flex-1 sm:flex-initial border-emerald-500 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
          >
            <RefreshCw className="w-4 h-4" />
            <span className="hidden sm:inline">Sync & Clear</span>
            <span className="sm:hidden">Sync</span>
          </Button>
          <Button 
            onClick={loadDashboardData}
            variant="outline"
            className="flex items-center gap-2 flex-1 sm:flex-initial"
            disabled={isLoading}
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            {isLoading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* Main Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card 
              key={index} 
              className="overflow-hidden border-0 shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer group"
              onClick={() => handleStatClick(stat.link)}
            >
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-muted-foreground mb-1">{stat.title}</p>
                    <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-foreground group-hover:text-teal-600 dark:group-hover:text-primary transition-colors">
                      {stat.value}
                    </p>
                  </div>
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 ${stat.bgColor} dark:bg-secondary rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform`}>
                    <Icon className={`w-6 h-6 sm:w-7 sm:h-7 ${stat.textColor}`} />
                  </div>
                </div>
                <div className="mt-3 sm:mt-4 flex items-center text-xs sm:text-sm text-teal-600 dark:text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  Click to view <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Revenue Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {revenueStats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card 
              key={index} 
              className="overflow-hidden border-0 shadow-md"
            >
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex-1">
                    <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-muted-foreground mb-1">{stat.title}</p>
                    <p className={`text-xl sm:text-2xl font-bold ${stat.color}`}>
                      {stat.value}
                    </p>
                  </div>
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-50 dark:bg-secondary rounded-xl flex items-center justify-center">
                    <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${stat.color}`} />
                  </div>
                </div>
                {stat.subtitle && (
                  <p className="text-xs text-gray-500 dark:text-muted-foreground mt-1">{stat.subtitle}</p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Lead Follow-ups & Upcoming Bookings */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Lead Follow-ups This Month */}
        <Card id="follow-ups" className="border-0 shadow-md">
          <CardHeader 
            className="border-b dark:border-border bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 cursor-pointer hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors p-4 sm:p-6"
            onClick={() => setFollowUpsExpanded(!followUpsExpanded)}
          >
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="text-base sm:text-lg flex items-center gap-2 flex-1 min-w-0 text-gray-900 dark:text-foreground">
                <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600 flex-shrink-0" />
                <span className="truncate">Lead Follow-ups This Month</span>
                <span className="text-xs sm:text-sm font-normal text-gray-600 dark:text-muted-foreground flex-shrink-0">({followUpLeads.length})</span>
              </CardTitle>
              <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                <Link to="/leads" onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="sm" className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 dark:hover:bg-orange-900/30 h-8 px-2 sm:px-3">
                    <span className="hidden sm:inline">View All</span>
                    <ArrowRight className="w-4 h-4 sm:ml-1" />
                  </Button>
                </Link>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 dark:hover:bg-orange-900/30"
                >
                  {followUpsExpanded ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </Button>
              </div>
            </div>
          </CardHeader>
          <div 
            className="overflow-hidden transition-all duration-300 ease-in-out"
            style={{ 
              maxHeight: followUpsExpanded ? '1000px' : '0px',
              opacity: followUpsExpanded ? 1 : 0
            }}
          >
            <CardContent className="p-6">
              <div className="space-y-4">
                {followUpLeads.length > 0 ? (
                  followUpLeads.slice(0, 5).map((lead) => (
                    <div 
                      key={lead.id} 
                      className="flex items-start space-x-4 p-4 rounded-lg bg-gray-50 dark:bg-secondary hover:bg-orange-50 dark:hover:bg-orange-900/20 hover:shadow-md transition-all cursor-pointer border-2 border-transparent hover:border-orange-200 dark:hover:border-orange-800"
                      onClick={() => handleLeadClick(lead)}
                    >
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-600 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Phone className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 dark:text-foreground">{lead.name}</p>
                        <p className="text-sm text-gray-600 dark:text-muted-foreground">{lead.phone}</p>
                        <div className="flex items-center space-x-4 mt-1">
                          <p className="text-xs text-gray-500 dark:text-muted-foreground">Follow-up: {formatDate(lead.followupDate)}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            lead.status === 'hot' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' :
                            lead.status === 'warm' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400' :
                            'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                          }`}>
                            {lead.status.toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500 dark:text-muted-foreground">{lead.eventType}</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-foreground">{lead.expectedGuests} guests</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-gray-500 dark:text-muted-foreground py-8">No follow-ups scheduled this month</p>
                )}
              </div>
            </CardContent>
          </div>
        </Card>
        {/* Upcoming Bookings */}
        <Card id="upcoming" className="border-0 shadow-md">
          <CardHeader 
            className="border-b dark:border-border bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 cursor-pointer hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
            onClick={() => setUpcomingExpanded(!upcomingExpanded)}
          >
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center space-x-2 text-gray-900 dark:text-foreground">
                <Calendar className="w-5 h-5 text-purple-600" />
                <span>Upcoming Bookings</span>
                <span className="ml-2 text-sm font-normal text-gray-600 dark:text-muted-foreground">({upcomingBookings.length})</span>
              </CardTitle>
              <div className="flex items-center space-x-2">
                <Link to="/bookings" onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="sm" className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 dark:hover:bg-purple-900/30">
                    View All <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                >
                  {upcomingExpanded ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </Button>
              </div>
            </div>
          </CardHeader>
          <div 
            className="overflow-hidden transition-all duration-300 ease-in-out"
            style={{ 
              maxHeight: upcomingExpanded ? '1000px' : '0px',
              opacity: upcomingExpanded ? 1 : 0
            }}
          >
            <CardContent className="p-6">
              <div className="space-y-4">
                {upcomingBookings.length > 0 ? (
                  upcomingBookings.slice(0, 5).map((booking) => (
                    <div key={booking.id} className="flex items-start space-x-4 p-4 rounded-lg bg-gray-50 dark:bg-secondary hover:bg-gray-100 dark:hover:bg-muted transition-colors">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center flex-shrink-0">
                        <CalendarCheck className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 dark:text-foreground truncate">{booking.venueName}</p>
                        <p className="text-sm text-gray-600 dark:text-muted-foreground">{booking.customerName}</p>
                        <div className="flex items-center space-x-4 mt-1">
                          <p className="text-xs text-gray-500 dark:text-muted-foreground">{formatDate(booking.eventDate)}</p>
                          <p className="text-xs font-medium text-purple-600">{booking.eventType}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        {!hideFinancials && (
                          <p className="text-sm font-bold text-gray-900 dark:text-foreground">{formatCurrency(booking.amount)}</p>
                        )}
                        <p className="text-xs text-gray-500 dark:text-muted-foreground">{booking.guests} guests</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-gray-500 dark:text-muted-foreground py-8">No upcoming bookings</p>
                )}
              </div>
            </CardContent>
          </div>
        </Card>
      </div>

      {/* Analytics Section */}
      <Card className="border-0 shadow-md">
        <CardHeader className="border-b dark:border-border bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20">
          <CardTitle className="text-xl text-gray-900 dark:text-foreground">Analytics & Reports</CardTitle>
          <p className="text-sm text-gray-600 dark:text-muted-foreground mt-1">View detailed insights and performance metrics</p>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Booking Analytics Card */}
            <Card 
              className="border-2 border-blue-100 dark:border-blue-900/50 hover:border-blue-300 dark:hover:border-blue-700 cursor-pointer transition-all duration-300 group"
              onClick={() => navigate('/booking-analytics')}
            >
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-14 h-14 bg-blue-50 dark:bg-blue-900/30 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <BarChart3 className="w-7 h-7 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-foreground text-lg mb-1">Booking Analytics</h3>
                    <p className="text-sm text-gray-600 dark:text-muted-foreground mb-3">Comprehensive insights into your booking performance</p>
                    <div className="flex items-center text-sm text-blue-600 font-medium">
                      View Report <ArrowRight className="w-4 h-4 ml-1" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Financial Analytics Card */}
            {!hideFinancials && (
              <Card 
                className="border-2 border-purple-100 dark:border-purple-900/50 hover:border-purple-300 dark:hover:border-purple-700 cursor-pointer transition-all duration-300 group"
                onClick={() => navigate('/financial-analytics')}
              >
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-14 h-14 bg-purple-50 dark:bg-purple-900/30 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                      <PieChart className="w-7 h-7 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 dark:text-foreground text-lg mb-1">Financial Analytics</h3>
                      <p className="text-sm text-gray-600 dark:text-muted-foreground mb-3">Track revenue, payments, and financial performance</p>
                      <div className="flex items-center text-sm text-purple-600 font-medium">
                        View Report <ArrowRight className="w-4 h-4 ml-1" />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Call Lead Confirmation Dialog */}
      <Dialog open={callDialogOpen} onOpenChange={setCallDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Phone className="w-5 h-5 text-orange-600" />
              <span>Call Lead?</span>
            </DialogTitle>
            <DialogDescription>
              Do you want to call this lead for follow-up?
            </DialogDescription>
          </DialogHeader>
          {selectedLead && (
            <div className="bg-gray-50 dark:bg-secondary rounded-lg p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600 dark:text-muted-foreground">Name:</span>
                <span className="text-sm font-semibold text-gray-900 dark:text-foreground">{selectedLead.name}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600 dark:text-muted-foreground">Phone:</span>
                <span className="text-sm font-semibold text-gray-900 dark:text-foreground">{selectedLead.phone}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600 dark:text-muted-foreground">Event:</span>
                <span className="text-sm font-semibold text-gray-900 dark:text-foreground">{selectedLead.eventType}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600">Status:</span>
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                  selectedLead.status === 'hot' ? 'bg-red-100 text-red-700' :
                  selectedLead.status === 'warm' ? 'bg-orange-100 text-orange-700' :
                  'bg-blue-100 text-blue-700'
                }`}>
                  {selectedLead.status.toUpperCase()}
                </span>
              </div>
            </div>
          )}
          <DialogFooter className="flex space-x-2 sm:space-x-2">
            <Button
              variant="outline"
              onClick={handleCallCancel}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCallConfirm}
              className="flex-1 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white"
            >
              <Phone className="w-4 h-4 mr-2" />
              Call Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sync & Clear Storage Dialog */}
      <Dialog open={syncDialogOpen} onOpenChange={setSyncDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-emerald-600" />
              Sync & Clear Local Storage
            </DialogTitle>
            <DialogDescription className="space-y-3 pt-2">
              <p className="text-sm">
                This will perform the following actions:
              </p>
              <ul className="text-sm space-y-2 list-disc list-inside text-gray-600">
                <li>Upload any pending local data to the server</li>
                <li>Clear all cached data from this device</li>
                <li>Download fresh data from central storage</li>
                <li>Ensure consistency across all your devices</li>
              </ul>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
                <p className="text-xs text-amber-800">
                  <strong>Note:</strong> Your login session will be preserved. The page will reload after syncing.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setSyncDialogOpen(false)}
              disabled={isSyncing}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSyncAndClear}
              disabled={isSyncing}
              className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white"
            >
              {isSyncing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Sync Now
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add to Home Screen Prompt */}
      <AddToHomeScreen />
    </div>
  );
};

export default Dashboard;